"use client"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import katex from "katex"
import "katex/dist/katex.min.css"
import {
  DEFAULT_EQUATION_LATEX,
  EQUATION_TABS,
  caretAfterInsert,
  latexForKatexPreview,
  type EquationSymbol,
  type EquationTabId,
} from "@/lib/ribbon/equation-symbols"

const ACCENT = "#4A90E2"
const BORDER = "#E0E0E0"
const POPUP_W = 700

function renderKatex(latex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode,
      output: "html",
      strict: "ignore",
    })
  } catch {
    return ""
  }
}

function KatexHtml({
  latex,
  display = false,
  className,
}: {
  latex: string
  display?: boolean
  className?: string
}) {
  const html = useMemo(
    () => renderKatex(latex, display),
    [latex, display],
  )
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

type EquationEditorPopupProps = {
  initialLatex?: string
  onClose: () => void
  onInsert: (latex: string) => void
}

export function EquationEditorPopup({
  initialLatex,
  onClose,
  onInsert,
}: EquationEditorPopupProps) {
  const [tabId, setTabId] = useState<EquationTabId>("basic")
  const [latex, setLatex] = useState(initialLatex?.trim() || DEFAULT_EQUATION_LATEX)
  const [isDragging, setIsDragging] = useState(false)
  const [pos, setPos] = useState(() => ({
    x: Math.max(16, Math.round((window.innerWidth - POPUP_W) / 2)),
    y: Math.max(24, Math.round(window.innerHeight * 0.08)),
  }))

  const popupRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const dragRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null)
  const pendingCaret = useRef<number | null>(null)

  const activeTab = EQUATION_TABS.find((t) => t.id === tabId) ?? EQUATION_TABS[0]

  const previewHtml = useMemo(() => {
    const src = latexForKatexPreview(latex)
    if (!src) return ""
    return renderKatex(src, true)
  }, [latex])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.focus()
    el.setSelectionRange(0, el.value.length)
  }, [])

  useLayoutEffect(() => {
    const el = textareaRef.current
    if (pendingCaret.current == null || !el) return
    el.focus()
    el.setSelectionRange(pendingCaret.current, pendingCaret.current)
    pendingCaret.current = null
  }, [latex])

  const onTitleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      e.preventDefault()
      setIsDragging(true)
      dragRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y }
    },
    [pos],
  )

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      const w = popupRef.current?.offsetWidth ?? POPUP_W
      const h = popupRef.current?.offsetHeight ?? 560
      const nextX = dragRef.current.px + (e.clientX - dragRef.current.mx)
      const nextY = dragRef.current.py + (e.clientY - dragRef.current.my)
      setPos({
        x: Math.min(Math.max(8, nextX), Math.max(8, window.innerWidth - w - 8)),
        y: Math.min(Math.max(8, nextY), Math.max(8, window.innerHeight - Math.min(h, 56))),
      })
    }
    const onUp = () => {
      setIsDragging(false)
      dragRef.current = null
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
    return () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
    }
  }, [isDragging])

  const insertSymbol = (symbol: EquationSymbol) => {
    const el = textareaRef.current
    const start = el?.selectionStart ?? latex.length
    const end = el?.selectionEnd ?? start
    const next = latex.slice(0, start) + symbol.latex + latex.slice(end)
    pendingCaret.current = start + caretAfterInsert(symbol.latex)
    setLatex(next)
  }

  const handleInsert = () => {
    const body = latex.trim()
    if (!body) return
    onInsert(body)
  }

  return createPortal(
    <div
      ref={popupRef}
      role="dialog"
      aria-labelledby="equation-editor-title"
      className="fixed z-[9999] flex flex-col overflow-hidden rounded-[10px] border bg-white shadow-[0_8px_32px_rgba(0,0,0,0.16)]"
      style={{
        left: pos.x,
        top: pos.y,
        width: POPUP_W,
        maxHeight: "calc(100vh - 16px)",
        borderColor: BORDER,
        fontFamily: "var(--font-inter, Inter, sans-serif)",
        userSelect: isDragging ? "none" : undefined,
      }}
    >
      {/* Header — drag handle */}
      <div
        className={`flex shrink-0 items-center justify-between border-b px-5 py-3 ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{ borderColor: BORDER }}
        onMouseDown={onTitleMouseDown}
      >
        <h2
          id="equation-editor-title"
          className="text-[18px] font-semibold leading-none"
          style={{ color: ACCENT }}
        >
          Equation Editor
        </h2>
        <button
          type="button"
          aria-label="Close"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:bg-gray-100 hover:text-[#4B5563]"
        >
          <X className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-3.5">
        {/* Category tabs */}
        <div className="flex items-center overflow-x-auto">
          {EQUATION_TABS.map((tab, i) => {
            const active = tab.id === tabId
            return (
              <div key={tab.id} className="flex items-center">
                {i > 0 && (
                  <span
                    className="mx-2 h-3.5 w-px shrink-0 bg-[#D1D5DB]"
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setTabId(tab.id)}
                  className="whitespace-nowrap px-0.5 text-[13px] leading-none transition-colors"
                  style={{
                    color: active ? ACCENT : "#1F2937",
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {tab.label}
                </button>
              </div>
            )
          })}
        </div>

        {/* Symbol palette */}
        <div
          className="flex flex-wrap gap-1.5 rounded-lg border bg-white p-2"
          style={{ borderColor: BORDER }}
        >
          {activeTab.symbols.map((symbol) => (
            <button
              key={symbol.id}
              type="button"
              title={`${symbol.label}  ${symbol.latex}`}
              aria-label={symbol.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertSymbol(symbol)}
              className="flex h-9 min-w-9 items-center justify-center rounded-md border bg-white px-2 text-[#111827] transition-colors hover:bg-[#F3F4F6] active:bg-[#E5E7EB]"
              style={{ borderColor: BORDER }}
            >
              <KatexHtml
                latex={symbol.display}
                className="eq-sym pointer-events-none [&_.katex]:text-[15px]"
              />
            </button>
          ))}
        </div>

        {/* LaTeX input */}
        <textarea
          ref={textareaRef}
          value={latex}
          onChange={(e) => setLatex(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault()
              handleInsert()
            }
          }}
          spellCheck={false}
          aria-label="LaTeX equation"
          className="min-h-[76px] w-full resize-y rounded-lg border bg-white px-3 py-2.5 text-[13px] leading-[1.55] text-[#111827] outline-none focus:border-[#4A90E2]"
          style={{ borderColor: BORDER }}
        />

        {/* Preview */}
        <div>
          <div className="mb-1.5 text-[13px] font-semibold text-[#1F2937]">
            Equation Preview:
          </div>
          <div
            className="flex min-h-[148px] items-center justify-center overflow-x-auto rounded-lg border bg-white px-4 py-5"
            style={{ borderColor: BORDER }}
          >
            {previewHtml ? (
              <div
                className="eq-preview text-[#111827] [&_.katex-display]:m-0 [&_.katex-display]:overflow-x-auto [&_.katex]:text-[1.28em]"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : (
              <span className="text-[13px] text-[#9CA3AF]">
                Type LaTeX to see a live preview
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex shrink-0 items-center justify-end gap-2.5 border-t px-5 py-3"
        style={{ borderColor: BORDER }}
      >
        <button
          type="button"
          onClick={onClose}
          className="h-9 rounded-md border bg-white px-4 text-[13px] font-medium transition-colors hover:bg-[#E8F2FC]"
          style={{ borderColor: ACCENT, color: ACCENT }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleInsert}
          disabled={!latex.trim()}
          className="h-9 rounded-md px-4 text-[13px] font-medium text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ backgroundColor: ACCENT }}
        >
          Insert Equation
        </button>
      </div>
    </div>,
    document.body,
  )
}
